import { useQuery } from '@tanstack/react-query';

import { SelectField } from './ui/index.tsx';
import { api } from '../lib/api.ts';
import type { Country } from '../lib/types.ts';

interface Props {
  value: string | null | undefined;
  onChange: (code: string) => void;
  label?: string;
  hint?: string;
  disabled?: boolean;
  /** Adds a blank option, for a party that inherits the business's country. */
  allowEmpty?: boolean;
  emptyLabel?: string;
}

/**
 * A country picker over the ISO alpha-2 codes the tax engine uses.
 *
 * This was a two-character text box, which is unusable unless you happen to
 * know that India is "IN" — the code is an implementation detail of the tax
 * rules and should never have been the thing a user types. The list comes from
 * the server so it is the same one validation checks against.
 */
export function CountryField({
  value,
  onChange,
  label = 'Country',
  hint,
  disabled,
  allowEmpty,
  emptyLabel = 'Same as your business',
}: Props) {
  const { data } = useQuery({
    queryKey: ['countries'],
    queryFn: () =>
      api.unscoped.get<{ countries: Country[]; commonCountryCodes: string[] }>(
        '/api/reference/jurisdictions',
      ),
    // A static list; fetch it once per session and never revalidate.
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const countries = data?.countries ?? [];
  const common = data?.commonCountryCodes ?? [];
  const shortlist = common
    .map((code) => countries.find((entry) => entry.code === code))
    .filter((entry): entry is Country => Boolean(entry));

  return (
    <SelectField
      label={label}
      hint={hint}
      value={value ?? ''}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
    >
      {allowEmpty && <option value="">{emptyLabel}</option>}

      {/* Until the list loads, keep the saved value selectable so the field
          never appears blank and never silently loses what is stored. */}
      {countries.length === 0 && value && <option value={value}>{value}</option>}

      {shortlist.length > 0 && (
        <optgroup label="Frequently used">
          {shortlist.map((entry) => (
            <option key={`common-${entry.code}`} value={entry.code}>
              {entry.name}
            </option>
          ))}
        </optgroup>
      )}

      {countries.length > 0 && (
        <optgroup label="All countries">
          {countries.map((entry) => (
            <option key={entry.code} value={entry.code}>
              {entry.name}
            </option>
          ))}
        </optgroup>
      )}
    </SelectField>
  );
}
